import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Button, Col, Form, Row, Select } from 'antd';
import axios from 'axios';
import PropTypes from 'prop-types';

import FloatingLabel from '../../../components/FloatingLabel/FloatingLabel';
import { UploadField } from '../../../components/SharedComponents/FormComponents/UploadField';

import { ArrowRight, ChevronDown, ChevronUp } from '../../../assets/icons';
import { getPreSignedUrl, updateUserProfile } from '../../../services/authApi/userApi';
import { addUserData } from '../../../store/reducer/accountSlice';
import { preventReload } from '../../../utils/preventReload';
import { toastNotify } from '../../../utils/toastNotify';
import { useIdentificationUpload } from '../../../Hooks/useIdentificationUpload';

const UploadID = (props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [arrowUp, setArrowUp] = useState(false);
  const dispatch = useDispatch();
  const counter = useSelector((state) => state.userData.counter);

  const {
    identificationType,
    setIdentificationType,
    fileListFront,
    fileListBack,
    fileListPassport,
    fileValidate,
    fileSizeValidate,
    fileFrontValidate,
    fileFrontSizeValidate,
    fileBackValidate,
    fileBackSizeValidate,
    onChangeFront,
    onChangeBack,
    onChangePassport,
    beforeUpload,
    handleRemoveUploaded,
    buildUploadsData,
    isUploadInvalid,
    isUploadIncomplete,
  } = useIdentificationUpload();

  useEffect(() => {
    preventReload(form);
  }, []);

  const onPreSignedUrlSuccess = async (resp, data, values) => {
    const preSignedUrl = resp?.data?.url;
    const storagePath = resp?.data?.storagePath;

    // Validate that we received the required data from pre-signed URL API
    if (!preSignedUrl || !storagePath) {
      throw new Error('Invalid pre-signed URL response: missing URL or storage path');
    }

    const config = {
      headers: {
        'Content-Type': data?.fileType,
      },
    };

    // Upload the file to S3 using the obtained pre-signed URL
    // Axios automatically throws an error for non-2xx status codes
    // Any errors will be caught and handled by the calling function (uploadImages)
    await axios.put(preSignedUrl, data?.file, config);

    let type, frontUrl, backUrl;

    if (data?.displayView === 'Front') {
      type = data?.docType;
      frontUrl = storagePath;
    } else {
      type = data?.docType;
      backUrl = storagePath;
    }

    return { docs: { type, frontUrl, backUrl } };
  };

  const onPreSignedUrlError = (error) => {
    const errorMessage = error?.response?.data?.message || 'An error occurred while uploading your document. Please try again';
    toastNotify('error', errorMessage);
    setLoading(false);
  };

  const handleUpdateUserProfile = async (uploadFormData) => {
    try {
      const resp = await updateUserProfile(uploadFormData);
      if (resp.isError) {
        toastNotify('error', resp?.message);
      } else {
        dispatch(addUserData({ userData: resp?.data?.user }));
        // After successful ID upload, go to StepFour
        props.nextAction('StepFour');
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const uploadImages = async (uploadsData) => {
    let uploadFailed = false;
    const results = {
      type: null,
      frontUrl: null,
      backUrl: null,
    };

    const promises = uploadsData.map(async (data) => {
      try {
        const resp = await getPreSignedUrl(data.fileType, data.docType, data.displayView);
        const preSignedResp = await onPreSignedUrlSuccess(resp, data);
        return preSignedResp?.docs;
      } catch (error) {
        uploadFailed = true;
        onPreSignedUrlError(error);
        return null;
      }
    });

    const uploadResults = await Promise.all(promises);

    // If any upload failed, return null to prevent profile update
    if (uploadFailed) {
      return null;
    }

    // Merge results from all uploads to avoid race condition
    uploadResults.forEach((result) => {
      if (result) {
        results.type = result.type || results.type;
        results.frontUrl = result.frontUrl || results.frontUrl;
        results.backUrl = result.backUrl || results.backUrl;
      }
    });

    // Validate that we have all required data
    if (!results.type) {
      toastNotify('error', 'Upload failed: Missing document type');
      return null;
    }

    // For passport, only frontUrl is required
    // For license, both frontUrl and backUrl are required
    if (!results.frontUrl) {
      toastNotify('error', 'Upload failed: Missing front image');
      return null;
    }

    if (results.type === 'Driving License' && !results.backUrl) {
      toastNotify('error', 'Upload failed: Missing back image');
      return null;
    }

    return {
      docs: {
        frontUrl: results.frontUrl,
        backUrl: results.backUrl,
        type: results.type,
      },
    };
  };

  const onUploadIDSubmit = async () => {
    setLoading(true);

    const imageUploadResp = await uploadImages(buildUploadsData());
    if (imageUploadResp) {
      // Only update the profile if uploads succeeded
      handleUpdateUserProfile(imageUploadResp);
    } else {
      setLoading(false);
    }
  };

  return (
    <>
      {counter > 4 ? (
        <>
          <p>You have already verified your ID. Please continue.</p>
          <Button
            type='primary'
            onClick={() => props.nextAction('StepFour')}
          >
            Continue <ArrowRight marginLeft='10px' verticalAlign='middle' top='-2px' />
          </Button>
        </>
      ) : (
        <Form
          form={form}
          className='auth-form uploadid'
          name='uploadIDForm'
          onFinish={onUploadIDSubmit}
        >
          <p>Please use one of the following valid forms of photo identification.</p>
          <ul style={{ marginLeft: '20px', marginBottom: '24px' }}>
            <li>U.S. State-Issued Driver's License</li>
            <li>U.S. State-Issued Identification Card (Non-Driver ID)</li>
            <li>U.S. Passport (Book)</li>
            <li>U.S. Passport (Card)</li>
            <li>U.S. Visa</li>
            <li>Federal Employment Authorization Card</li>
          </ul>
          <p>Please upload a valid, US government-issued ID. The image must be clear and easy to read. Accepted file types: JPEG, JPG, or PNG.</p>
          <FloatingLabel label='Type of identification' inputId='identification' inputValue={identificationType}>
            <>
              <div id='identification-anchor' />
              <Form.Item name='identification' rules={[{ required: true, message: 'Please select identification type' }]}>
                <Select
                  disabled={loading}
                  id='identification'
                  onChange={(value) => setIdentificationType(value)}
                  value={identificationType}
                  getPopupContainer={() => document.getElementById('identification-anchor')}
                  onDropdownVisibleChange={() => setArrowUp(!arrowUp)}
                  suffixIcon={arrowUp ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
                >
                  <Select.Option value='passport'>Passport</Select.Option>
                  <Select.Option value='license'>ID</Select.Option>
                </Select>
              </Form.Item>
            </>
          </FloatingLabel>
          {identificationType === 'license' && (
            <Row gutter={20}>
              <Col span={24} md={12}>
                <label htmlFor='licenseFront' className='sr-only'>
                  Upload front of ID
                </label>
                <Form.Item className='license-front'>
                  <UploadField
                    onChangeHandle={onChangeFront}
                    fileList={fileListFront}
                    id='licenseFront'
                    beforeUpload={(value) => beforeUpload(value, 'front')}
                    handleRemoveUploaded={() => handleRemoveUploaded(fileListFront[0].uid, 'front')}
                    label='Upload front of ID'
                    fileValidate={fileFrontValidate}
                    fileSizeValidate={fileFrontSizeValidate}
                    loading={loading}
                  />
                </Form.Item>
              </Col>
              <Col span={24} md={12}>
                <label htmlFor='licenseBack' className='sr-only'>
                  Upload back of ID
                </label>
                <Form.Item className='license-back'>
                  <UploadField
                    onChangeHandle={onChangeBack}
                    fileList={fileListBack}
                    id='licenseBack'
                    beforeUpload={(value) => beforeUpload(value, 'back')}
                    handleRemoveUploaded={() => handleRemoveUploaded(fileListBack[0].uid, 'back')}
                    label='Upload back of ID'
                    fileValidate={fileBackValidate}
                    fileSizeValidate={fileBackSizeValidate}
                    loading={loading}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          {identificationType === 'passport' && (
            <Row>
              <Col span={24} md={12}>
                <label htmlFor='passport' className='sr-only'>
                  Upload ID
                </label>
                <Form.Item className='passport'>
                  <UploadField
                    onChangeHandle={onChangePassport}
                    fileList={fileListPassport}
                    id='passport'
                    beforeUpload={(value) => beforeUpload(value, 'passport')}
                    handleRemoveUploaded={() => !loading && handleRemoveUploaded(fileListPassport[0].uid, 'passport')}
                    label='Upload ID'
                    fileValidate={fileValidate}
                    fileSizeValidate={fileSizeValidate}
                    loading={loading}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Form.Item className='auth-form__fixed-submit'>
            <Button
              type='primary'
              htmlType='submit'
              className='ant-btn-primary--alt'
              loading={loading}
              disabled={isUploadIncomplete || isUploadInvalid}
            >
              Continue <ArrowRight marginLeft='10px' verticalAlign='middle' top='-2px' />
            </Button>
          </Form.Item>
        </Form>
      )}
    </>
  );
};

UploadID.propTypes = {
  nextAction: PropTypes.func,
};

export default UploadID;
